import { useMemo } from 'react';
import type { Message } from '@ltvco/refresh-lib/v2';
import type { VehicleForecast } from '@ltvco/refresh-lib/api';
import { MessagePairType } from 'components/ChatAI/VehicleMatchmaker/hooks/useMessagePairing';

interface ValueForecastData extends VehicleForecast {
  error?: string;
  [key: string]: unknown;
}

type MessageWithValueForecast = Message & {
  valueForecastData?: ValueForecastData;
};

interface UseMessagePairingReturn {
  pairs: MessagePairType[];
  firstValueForecastAssistantIndex: number;
  firstValueForecastData: VehicleForecast | null | undefined;
  hasError: boolean;
}

/**
 * Custom hook to handle message pairing logic
 * Pairs user messages with assistant responses and tracks forecast data
 */
export const useMessagePairing = (
  messages: Message[],
): UseMessagePairingReturn => {
  return useMemo(() => {
    const messagesWithValueForecast = messages as MessageWithValueForecast[];

    // Find first successful forecast
    const firstValueForecastAssistantIndex =
      messagesWithValueForecast.findIndex(
        (message) =>
          message.role === 'assistant' &&
          message.valueForecastData &&
          !message.valueForecastData.error,
      );

    const firstValueForecastData =
      firstValueForecastAssistantIndex !== -1
        ? messagesWithValueForecast[firstValueForecastAssistantIndex]
            .valueForecastData
        : null;

    // Check for errors - only consider errors related to forecast attempts
    // If there's a successful forecast, only check for errors before or at that index
    // Messages after a successful forecast are follow-up conversations and shouldn't affect hasError
    const messagesToCheck =
      firstValueForecastAssistantIndex !== -1
        ? messagesWithValueForecast.slice(
            0,
            firstValueForecastAssistantIndex + 1,
          )
        : messagesWithValueForecast;
    const hasError = messagesToCheck.some(
      // If there's a successful forecast, only check messages up to that point
      (msg) =>
        msg.role === 'assistant' &&
        ((msg.valueForecastData && msg.valueForecastData.error) ||
          (msg.content && msg.valueForecastData == null)),
    );

    // Create pairs
    const pairs: MessagePairType[] = [];
    for (let i = 0; i < messagesWithValueForecast.length; i++) {
      const userMsg = messagesWithValueForecast[i]!;
      if (userMsg.role !== 'user') continue;

      const maybeAssistant = messagesWithValueForecast[i + 1];
      const assistantMsg =
        maybeAssistant && maybeAssistant.role === 'assistant'
          ? maybeAssistant
          : null;
      const assistantIndex = assistantMsg ? i + 1 : null;

      // Skip rendering the pair whose assistant message is rendered
      // as the ValueForecasterResultCard to avoid extra vertical spacing.
      if (
        assistantIndex !== null &&
        assistantIndex === firstValueForecastAssistantIndex
      ) {
        continue;
      }

      pairs.push({
        id: `pair-${i}`,
        user: userMsg,
        assistant: assistantMsg,
        userIndex: i,
        assistantIndex,
      });
    }

    return {
      pairs,
      firstValueForecastAssistantIndex,
      firstValueForecastData,
      hasError,
    };
  }, [messages]);
};
